import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {IOverlaySnapshot, Overlay} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Overlay';
import {ISurfaceLayerSnapshot, SurfaceLayer} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceLayer';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';

export type PageId = GUID;
export type PageName = string;

export interface IPageSnapshot
{
    id:PageId;
    name:PageName;
    sectionName:string;
    artboard:ISurfaceLayerSnapshot;
    overlays:IOverlaySnapshot[];
}

export class PageUiState
{
    @observable
    public activeOverlay:Overlay;
}

export class Page
{
    public id:PageId;

    @observable
    public artboard:SurfaceLayer;

    @observable
    public name:string;

    @observable
    public sectionName:string;

    @observable
    public overlays:Overlay[];

    public uiState:PageUiState = new PageUiState();

    private constructor()
    {
    }

    public get activeOverlay():Overlay
    {
        return this.uiState.activeOverlay;
    }

    public static create(name:string, boxes:Array<Box> = []):Page
    {
        const page:Page = new Page();

        page.id = IdUtil.newGUID();
        page.artboard = SurfaceLayer.create(boxes);
        page.overlays = [];
        page.name = name;
        page.sectionName = null;

        return page;
    }

    @action.bound
    public addOverlay(overlay:Overlay, currentIndex:number):void
    {
        if( currentIndex > -1 )
        {
            if( !this.overlays[currentIndex].isPinned )
            {
                const shouldChangeActiveOverlay:boolean = this.activeOverlay === this.overlays[currentIndex];

                this.overlays.splice(currentIndex, 1);
                this.overlays.push(overlay);

                if( shouldChangeActiveOverlay )
                    this.setActiveOverlay(overlay);
            }
        }
        else
            this.overlays.push(overlay);
    }

    @action.bound
    public removeOverlay(index:number):Page | null
    {
        if( index > -1 )
        {
            const shouldChangeActiveOverlay:boolean = this.activeOverlay === this.overlays[index];
            const pageToReturn:Page = this.overlays[index].page;

            this.overlays.splice(index, 1);

            if( shouldChangeActiveOverlay )
                this.setActiveOverlay(this._getNewActiveOverlay(index));

            return pageToReturn;
        }

        return null;
    }

    @action.bound
    public removeOverlaysByPredicate(predicate:(overlay:Overlay, index:number) => boolean):void
    {
        let previousActiveOverlayIndex:number = -1;

        this.overlays = this.overlays.reduce((res:Overlay[], overlay:Overlay, index:number) =>
        {
            if( !predicate(overlay, index) )
                res.push(overlay);
            else if( overlay === this.activeOverlay )
                previousActiveOverlayIndex = index;

            return res;
        }, []);

        if( previousActiveOverlayIndex > -1 )
            this.setActiveOverlay(this._getNewActiveOverlay(previousActiveOverlayIndex));
    }

    @bind
    private _getNewActiveOverlay(previousActiveOverlayIndex:number):Overlay
    {
        const {length} = this.overlays;

        if( length === 0 )
            return null;
        else if( length - 1 < previousActiveOverlayIndex )
            return this.overlays[length - 1];

        return this.overlays[previousActiveOverlayIndex];
    }

    @action.bound
    public moveOverlay(overlay:Overlay, insertIndex:number):void
    {
        const currentIndex:number = this.overlays.findIndex((item:Overlay) => item === overlay);

        if( currentIndex >= 0 )
        {
            const overlaysCopy = this.overlays.slice();

            const currentOverlay:Overlay = overlaysCopy.splice(currentIndex, 1)[0];
            overlaysCopy.splice(insertIndex, 0, currentOverlay);

            this.overlays = overlaysCopy;
        }
    }

    @action.bound
    public setActiveOverlay(overlay:Overlay):void
    {
        this.uiState.activeOverlay = overlay;
    }

    public static toJson:ITransformer<Page, IPageSnapshot> = createTransformer((page:Page) =>
    {
        return {
            id: page.id,
            name: page.name,
            sectionName: page.sectionName,
            artboard: SurfaceLayer.toJson(page.artboard),
            overlays: page.overlays.map((overlay:Overlay) => Overlay.toJson(overlay))
        };
    });

    public static fromJson(json:IPageSnapshot | null,
                           currentState?:Page,
                           currentJson?:IPageSnapshot,
                           ctx?:DesignStateFromJsonCtx):Page
    {
        if( !currentState )
            return Page.fromJsonDeep(json);

        if( !currentJson )
            currentJson = Page.toJson(currentState);

        const page:Page = new Page();

        if( json )
        {
            page.id = json.id;
            page.name = json.name;
            page.sectionName = json.sectionName;

            if( json.overlays === currentJson.overlays )
                page.overlays = currentState.overlays;
            else
            {
                page.overlays = json.overlays.map((_, index) =>
                {
                    return json.overlays[index] === currentJson.overlays[index]
                        ? currentState.overlays[index]
                        : Overlay.fromJson(json.overlays[index]);
                });
            }

            page.artboard = SurfaceLayer.fromJson(json.artboard, page, currentState.artboard, currentJson.artboard, ctx);
        }

        Page._restoreUiState(page, currentState);

        return page;
    }

    public static fromJsonDeep(json:IPageSnapshot | null):Page
    {
        const page:Page = new Page();

        if( json )
        {
            page.id = json.id;
            page.name = json.name;
            page.sectionName = json.sectionName;
            page.overlays = json.overlays.map((overlaySnapshot:IOverlaySnapshot) => Overlay.fromJsonDeep(overlaySnapshot));
            page.artboard = SurfaceLayer.fromJsonDeep(json.artboard, page);
        }

        return page;
    }

    private static _restoreUiState(state:Page, currentState:Page):void
    {
        const {activeOverlay} = currentState.uiState;

        state.uiState.activeOverlay = activeOverlay
            ? state.overlays.find(({page: {id}}) => activeOverlay.page.id === id)
            : null;
    }
}
